import "./ScrollReveal.scss";
import { useLayoutEffect } from "react";
import { useLocation } from "react-router-dom";

/** Content blocks that fade in on load and as they scroll into view, across all pages. */
const REVEAL_SELECTOR = [
	// Home
	".hero-container .hero-content-container",
	".hero-container .hero-image",
	".home-intro-section > .text-container",
	".spec-tile__media",
	".spec-tile__content",
	".spec-column-tile__media",
	".spec-column-tile__content",
	".enrollment-steps-section > .text-container",
	".enrollment-steps__item",
	".footer-container .column",
	// Heavy Up
	".hu-video-card",
	".hu-investment-section > *",
	".hu-collage-image",
	".hu-plans-inner > .text-container",
	".hu-plan-card",
	// Automated Offers
	".automated-offers-page .hero-panel",
	".automated-offers-page .statement p",
	".automated-offers-page .section-head",
	".automated-offers-page .step",
	".automated-offers-page .included-collage",
	".automated-offers-page .offer-stack",
	".automated-offers-page .inc-group",
	".automated-offers-page .pkg",
	".automated-offers-page .volume",
	".automated-offers-page .tl",
	".automated-offers-page .form-grid > *",
	".site-footer__inner > *",
].join(",");

const STAGGER_MS = 80;
const MAX_STAGGER_STEPS = 5;

function ScrollReveal() {
	const { pathname } = useLocation();

	// Layout effect: classes are applied before paint, so content doesn't flash in and then hide.
	useLayoutEffect(() => {
		const reduceMotion = window.matchMedia(
			"(prefers-reduced-motion: reduce)",
		).matches;
		if (reduceMotion || !("IntersectionObserver" in window)) return undefined;

		const elements = [...document.querySelectorAll(REVEAL_SELECTOR)];
		elements.forEach((el) => {
			// Items side by side in the same container (cards, steps) appear one after another.
			const siblings = [...el.parentElement.children].filter((child) =>
				child.matches(REVEAL_SELECTOR),
			);
			const step = Math.min(siblings.indexOf(el), MAX_STAGGER_STEPS);
			el.style.setProperty("--reveal-delay", `${step * STAGGER_MS}ms`);
			el.classList.add("reveal");
		});

		const observer = new IntersectionObserver(
			(entries) => {
				entries.forEach((entry) => {
					if (entry.isIntersecting) {
						entry.target.classList.add("reveal--visible");
					} else if (entry.boundingClientRect.top > 0) {
						// Left through the bottom (scrolling up): reset so it fades in again on the way down.
						// Elements that leave through the top stay visible.
						entry.target.classList.remove("reveal--visible");
					}
				});
			},
			{ threshold: 0.12, rootMargin: "0px 0px -40px 0px" },
		);
		elements.forEach((el) => observer.observe(el));

		return () => observer.disconnect();
	}, [pathname]);

	return null;
}

export default ScrollReveal;
